import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import {
  formatDurationSince,
  formatRelativeTime,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeViewErrorCardProps = ComponentProps<"div"> & {
  target: UptimeTargetFragment
}

function UptimeViewErrorCard(props: UptimeViewErrorCardProps) {
  const { target, className, ...divProps } = props

  const isDown = target.LatestStatus === "down"
  const hasHistoricalError = target.LastError != null

  if (!isDown && !hasHistoricalError) return null

  // Prominent red card when currently down; subdued neutral card
  // when the target is currently up but we still want to surface
  // the last captured failure for context.
  return (
    <div
      className={cn(
        "uptime-view-chart-frame flex flex-col gap-3",
        isDown && "border-destructive/60 bg-destructive/5",
        className,
      )}
      {...divProps}
    >
      <div className="flex items-center gap-2">
        <Icon
          src={isDown ? "triangle-warning" : "clock"}
          className={cn(
            "text-base",
            isDown ? "text-destructive" : "text-muted-foreground",
          )}
        />
        <div
          className={cn(
            "font-medium",
            isDown ? "text-destructive" : "text-foreground",
          )}
        >
          {isDown ? "Currently failing" : "Last failure"}
        </div>
        <div className="flex-1" />
        {isDown && target.CurrentStatusSince && (
          <div className="text-xs text-muted-foreground tabular-nums">
            down for {formatDurationSince(target.CurrentStatusSince)}
          </div>
        )}
        {!isDown && target.LastErrorAt && (
          <div className="text-xs text-muted-foreground tabular-nums">
            {formatRelativeTime(target.LastErrorAt)}
          </div>
        )}
      </div>

      <div className="flex items-start gap-3 flex-wrap">
        {target.LastErrorHttpStatus != null && (
          <div
            className={cn(
              "inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-mono tabular-nums",
              isDown
                ? "border-destructive/50 text-destructive"
                : "border-border text-muted-foreground",
            )}
          >
            <span className="text-[10px] uppercase tracking-wider opacity-70">
              HTTP
            </span>
            <span className="text-sm font-semibold">
              {target.LastErrorHttpStatus}
            </span>
          </div>
        )}
        {target.LastError && (
          <pre
            className={cn(
              "flex-1 min-w-0 text-xs font-mono p-3 rounded border whitespace-pre-wrap break-words",
              isDown
                ? "border-destructive/40 text-destructive bg-destructive/5"
                : "border-border text-muted-foreground bg-muted/20",
            )}
          >
            {target.LastError}
          </pre>
        )}
      </div>
    </div>
  )
}

export default UptimeViewErrorCard
